import {Annotation, ChangeSet, type ChangeSpec, EditorState, type Extension} from '@codemirror/state'

import {
  buildContinuationSyntax,
  type EscapeContinuationMode,
  removeEscapedPrefixes,
} from '../../language-tools/markdown/continuation-utils'
import {handleListItemPaste} from '../../language-tools/markdown/list-utils'
import {MarkdownLine} from '../../language-tools/markdown/markdown-line'
import {parseMarkdown} from '../../language-tools/markdown/parse-markdown'
import {getLineRanges} from '../../language-tools/source-utils'
import {getMarkdownTree, markdownParserExtension} from './markdown-parser'
import {createText} from './single-line-editor'

/**
 * Annotation to apply to a transaction to indicate that Markdown continuation logic should be skipped to allow the
 * user to escape some Markdown syntax. Will skip up to the last instance of the matching prefix. For example, if
 * the annotation is `escapeList` and the user is inside a blockquote inside a list item, both the blockquote
 * and the list will be escaped.
 */
export const escapeMarkdownContinuation = Annotation.define<EscapeContinuationMode>()

/**
 * Transaction filter that maintains the current Markdown node by prepending each line of the inserted text with
 * prefixes that match the current line. Control behavior with `escapeMarkdownContinuation` annotations on the
 * transaction.
 *
 * Using a transaction filter for this (rather than a keybinding) allows us to process pasted, dropped, or otherwise
 * inserted text in addition to simple enter key presses.
 *
 * Currently handles blockquotes, list items, and indented code blocks - all the multiline syntax that is defined with
 * line prefixes (rather than block wrappers).
 */
const markdownContinuationFilter = EditorState.transactionFilter.of(tr => {
  const escapeAnnotation = tr.annotation(escapeMarkdownContinuation)

  // Transaction filters are blocking operations, so we want to early-exit them as fast as possible if we can
  if (!tr.isUserEvent('input') || !tr.docChanged || tr.changes.empty || escapeAnnotation === 'all') return tr

  const startTree = getMarkdownTree(tr.startState)
  const source = tr.startState.doc.toString()

  /** Will be a set of insertions that can be applied after the original changes to add the continuation syntax. */
  const newChanges: ChangeSpec[] = []
  tr.changes.iterChanges((fromA, toA, fromB, toB, inserted) => {
    const text = inserted.toString()

    // Skip all the calculations if the change has no newlines
    if (!text.includes('\n')) return

    // fromA is used to get the current context, because the `source` and `startTree` are the state of the document
    // before the change was made. We avoiud calculating the post-change document since we are going to change it again,
    // so that would be wated computation.
    const currentLine = MarkdownLine.atOffset(startTree, source, fromA)
    const prefixes = removeEscapedPrefixes(currentLine?.prefixesBefore(fromA) ?? [], escapeAnnotation)
    if (!currentLine || prefixes.length === 0) return

    const continuationSyntax = buildContinuationSyntax(prefixes)
    if (continuationSyntax === '') return

    // Context for inserted text
    const insertedTree = parseMarkdown(text)
    const insertedLines = getLineRanges(text, 0, text.length)
    const isInsertingIntoListItem =
      currentLine.lastPrefix?.tokenType === 'listItemPrefix' || currentLine.lastPrefix?.tokenType === 'taskListCheck'

    // Process each line after the first (the first line continues the existing content)
    for (let i = 1; i < insertedLines.length; i++) {
      const line = insertedLines[i]
      if (!line) continue

      // "Smartly" handle inserting list items into existing list item
      if (isInsertingIntoListItem && (tr.isUserEvent('input.paste') || tr.isUserEvent('input.drop'))) {
        const insertedMarkdownLine = MarkdownLine.atOffset(insertedTree, text, line.start)
        const insertedPrefixes = insertedMarkdownLine?.prefixes ?? []
        const changes = handleListItemPaste(fromB, [...currentLine.prefixes], [...insertedPrefixes])
        if (changes.length > 0) {
          newChanges.push(...changes)
          continue
        }
      }

      // Handle default continuation for non-list contexts
      newChanges.push({
        // When making the change we use fromB (the position in the new document) to calculate the insertion point
        // because in the case of multiple changes, any insertions after the first will have shifted positions.
        from: fromB + line.start,
        insert: createText(continuationSyntax),
      })
      continue
    }
  })

  const newChangeSet = ChangeSet.of(newChanges, tr.newDoc.length)
  return [
    tr,
    {
      // These new changes stack on top of the original changes
      sequential: true,
      changes: newChangeSet,
      // Ensure we don't rerun continuation on this transaction
      annotations: [escapeMarkdownContinuation.of('all')],
      // Map the new selection through the additional changes
      // Setting assoc=1 causes the cursor to be associated with inserted text (text following it), thus ending up after it
      selection: tr.newSelection.map(newChangeSet, 1),
    },
  ]
})

/**
 * Automatically continues the current Markdown syntax as the user inserts new lines of content.
 */
export const markdownContinuationExtension: Extension = [markdownParserExtension, markdownContinuationFilter]
